import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout'
import { ClienteCombobox } from '@/components/ui/cliente-combobox'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { RefreshCw, Inbox } from 'lucide-react'
import {
  ComunicacionesList,
  ComunicacionDetalleDialog,
  FiltrosCasillero,
} from '@/components/casillero'
import {
  useComunicaciones,
  useConsumirComunicacion,
} from '@/hooks/useCasilleroElectronico'
import { useClientes } from '@/hooks/useClientes'
import { usePermissions } from '@/hooks/usePermissions'
import { seAbreSinConfirmar } from '@/lib/casillero'
import { ResumenComunicaciones } from '@/components/casillero/ResumenComunicaciones'
import type {
  ComunicacionResumen,
  ComunicacionDetalle,
  FiltrosCasillero as FiltrosType,
} from '@/types/afip'

export function CasilleroElectronico() {
  // Client selection
  const { data: clientes = [] } = useClientes()
  const [clienteId, setClienteId] = useState('')

  // Filters
  const [filtros, setFiltros] = useState<FiltrosType>({})

  // Los filtros se vacían junto con el cambio de cliente, no después. `key` remonta el bloque de
  // filtros en el mismo render, y lo que reciba ahí es lo que muestra.
  const cambiarCliente = (id: string) => {
    setClienteId(id)
    setFiltros({})
  }

  // Deep-link defensivo: ?cuit=XXX preselecciona SOLO si pertenece a la org del usuario.
  // useClientes() ya devuelve clientes filtrados por org, así que si el CUIT no está en
  // `clientes`, no es de mi org y se muestra warning.
  const [searchParams, setSearchParams] = useSearchParams()
  const cuitFromUrl = searchParams.get('cuit')
  useEffect(() => {
    if (!cuitFromUrl || clientes.length === 0 || clienteId) return
    const cuitLimpio = cuitFromUrl.replace(/\D/g, '')
    if (cuitLimpio.length !== 11) return
    const match = clientes.find(
      (c) => c.cuit != null && String(c.cuit).replace(/\D/g, '') === cuitLimpio,
    )
    if (match?.id) {
      cambiarCliente(match.id)
    } else {
      toast.warning(
        'El CUIT del enlace no corresponde a un cliente registrado en tu organización',
      )
      // Limpiar el query param para evitar repetir el toast en re-renders.
      // setSearchParams es estable (memoizado por React Router) así que no entra en deps.
      const next = new URLSearchParams(window.location.search)
      next.delete('cuit')
      setSearchParams(next, { replace: true })
    }
    // Intencionalmente sin searchParams/setSearchParams — son referencialmente estables
    // y agregarlos crearía un loop con el setSearchParams del else branch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuitFromUrl, clientes, clienteId])

  const selectedCliente = useMemo(
    () => clientes.find((c) => c.id === clienteId),
    [clientes, clienteId],
  )
  const cuitCliente = selectedCliente?.cuit
    ? String(selectedCliente.cuit).replace(/\D/g, '')
    : null

  // Data
  const {
    data: resultado,
    isLoading,
    error,
    refetch,
  } = useComunicaciones(cuitCliente, filtros)

  // Detail dialog
  const [comunicacionSel, setComunicacionSel] =
    useState<ComunicacionResumen | null>(null)
  const [detalleCargado, setDetalleCargado] =
    useState<ComunicacionDetalle | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const consumirMutation = useConsumirComunicacion()
  const { puede } = usePermissions()

  const handleVerDetalle = (comunicacion: ComunicacionResumen) => {
    setComunicacionSel(comunicacion)
    setDetalleCargado(null)
    setDialogOpen(true)

    // Una comunicación ya leída se carga sola: abrirla otra vez no vuelve a perfeccionar la
    // notificación. En cualquier otro estado el diálogo espera la confirmación legal.
    if (seAbreSinConfirmar(comunicacion.estado)) {
      handleConfirmarAbrir(comunicacion)
    }
  }

  const handleConfirmarAbrir = (comunicacion: ComunicacionResumen) => {
    if (!cuitCliente) return
    consumirMutation.mutate(
      { cuit: cuitCliente, comunicacionId: comunicacion.comunicacionId },
      {
        onSuccess: (data) => {
          setDetalleCargado(data)
        },
      },
    )
  }

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setComunicacionSel(null)
      setDetalleCargado(null)
    }
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Casillero Electrónico"
        description="Comunicaciones del Domicilio Fiscal Electrónico de ARCA — RG (AFIP) 4280/2018"
      />

      <div className="flex-1 flex flex-col p-6 gap-6 min-h-0">
        {/* Selector de cliente + filtros */}
        <div className="shrink-0 flex flex-col gap-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-1 w-full sm:w-[350px]">
              <label className="text-xs text-muted-foreground">Cliente</label>
              <ClienteCombobox
                clientes={clientes}
                value={clienteId}
                onValueChange={cambiarCliente}
                placeholder="Seleccionar cliente..."
              />
            </div>

            <FiltrosCasillero
              key={clienteId}
              filtros={filtros}
              onChange={setFiltros}
              disabled={!cuitCliente}
            />

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => refetch()}
                    disabled={!cuitCliente || isLoading}
                    aria-label="Actualizar comunicaciones desde AFIP"
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Actualizar desde AFIP</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Un rango de fechas más largo del que ARCA acepta vuelve como error. Sin mostrarlo, la
              consulta rechazada se lee igual que una sin resultados. */}
          {cuitCliente && error && (
            <p className="text-sm text-destructive">{error.message}</p>
          )}

          {cuitCliente && resultado && <ResumenComunicaciones resultado={resultado} />}
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 flex flex-col">
          {!cuitCliente ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-2">
                <Inbox className="h-12 w-12 text-muted-foreground/50 mx-auto" />
                <p className="text-muted-foreground">
                  Seleccioná un cliente para ver sus comunicaciones fiscales
                </p>
                <p className="text-xs text-muted-foreground">
                  El cliente debe tener delegado el servicio &quot;Consulta y
                  lectura de Comunicaciones&quot; al CUIT del estudio.
                </p>
              </div>
            </div>
          ) : (
            <ComunicacionesList
              comunicaciones={resultado?.comunicaciones ?? []}
              isLoading={isLoading}
              onVerDetalle={handleVerDetalle}
            />
          )}
        </div>
      </div>

      {/* Detail dialog */}
      <ComunicacionDetalleDialog
        comunicacion={comunicacionSel}
        detalle={detalleCargado}
        isLoading={consumirMutation.isPending}
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        onConfirmarAbrir={handleConfirmarAbrir}
        puedeAbrir={puede.abrirComunicacionARCA}
        cuitContribuyente={cuitCliente}
      />
    </div>
  )
}
